import { Link } from "react-router-dom";
import FooterDisclaimer from "./FooterDisclaimer";
import NetworkFooter from "./NetworkFooter";
import InboundReciprocalLink from "./InboundReciprocalLink";
import { REVIEWER, SHRINKMD_URL, networkUrl } from "@/lib/site";

export default function Footer() {
  const year = new Date().getFullYear();
  const linkStyle = { color: "hsl(var(--footer-fg))" };
  const headingStyle = {
    color: "hsl(var(--brand))",
  } as const;

  return (
    <footer
      className="mt-24"
      style={{
        background: "hsl(var(--footer-bg))",
        color: "hsl(var(--footer-fg))",
      }}
    >
      <FooterDisclaimer />

      <div className="mx-auto max-w-[1200px] px-6 py-14">
        <div className="flex items-center gap-3 mb-10">
          <svg
            width="44"
            height="44"
            viewBox="0 0 44 44"
            role="img"
            aria-label="AnxietyResource monogram"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="1"
              y="1"
              width="42"
              height="42"
              rx="4"
              fill="transparent"
              stroke="hsl(var(--brand))"
              strokeWidth="2"
            />
            <g
              fill="none"
              stroke="hsl(var(--footer-fg))"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="11" x2="22" y2="33" />
              <line x1="9" y1="33" x2="22" y2="11" />
              <line x1="13.5" y1="24" x2="22" y2="24" />
              <path d="M22 11 H27 C31.5 11 31.5 22 27 22 H22" />
              <line x1="22" y1="22" x2="31" y2="33" />
            </g>
            <circle cx="36" cy="32" r="2.6" fill="hsl(var(--brand))" />
          </svg>
          <div className="font-serif text-xl font-semibold tracking-tight">
            AnxietyResource
            <span style={{ color: "hsl(var(--brand))", fontWeight: 600 }}>
              .org
            </span>
          </div>
        </div>

        <div className="grid gap-10 sm:grid-cols-2 md:grid-cols-4">
          {/* Column 1, Site */}
          <div>
            <div
              className="text-xs font-semibold uppercase tracking-widest mb-3"
              style={headingStyle}
            >
              Site
            </div>
            <ul className="space-y-2 text-sm list-none p-0 m-0">
              <li>
                <Link to="/about" style={linkStyle} className="hover:underline">
                  About
                </Link>
              </li>
              <li>
                <Link to="/media" style={linkStyle} className="hover:underline">
                  Media
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  style={linkStyle}
                  className="hover:underline"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  to="/verified"
                  style={linkStyle}
                  className="hover:underline"
                >
                  Verify us
                </Link>
              </li>
              <li>
                <Link
                  to="/glossary"
                  style={linkStyle}
                  className="hover:underline"
                >
                  Glossary
                </Link>
              </li>
              <li>
                <Link
                  to="/anxiety-faq"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-anxiety-faq"
                >
                  Anxiety FAQ
                </Link>
              </li>
              <li>
                <Link
                  to="/resources"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-resources"
                >
                  Resources
                </Link>
              </li>
              <li>
                <Link
                  to="/contributors"
                  style={linkStyle}
                  className="hover:underline"
                >
                  Contributors
                </Link>
              </li>
              <li>
                <Link
                  to="/medical-review-board"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-medical-review-board"
                >
                  Medical review board
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 2, Topics */}
          <div>
            <div
              className="text-xs font-semibold uppercase tracking-widest mb-3"
              style={headingStyle}
            >
              Topics
            </div>
            <ul className="space-y-2 text-sm list-none p-0 m-0">
              <li>
                <Link
                  to="/symptoms"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-symptoms"
                >
                  Symptoms
                </Link>
              </li>
              <li>
                <Link
                  to="/conditions"
                  style={linkStyle}
                  className="hover:underline"
                >
                  Conditions
                </Link>
              </li>
              <li>
                <Link
                  to="/work-performance"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-work-performance"
                >
                  Work &amp; performance
                </Link>
              </li>
              <li>
                <Link
                  to="/treatment-education"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-treatment-education"
                >
                  Treatment, explained
                </Link>
              </li>
              <li>
                <Link
                  to="/overthinking-rumination"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-overthinking-rumination"
                >
                  Overthinking &amp; rumination
                </Link>
              </li>
              <li>
                <Link
                  to="/panic-nervous-system"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-panic-nervous-system"
                >
                  Panic &amp; the nervous system
                </Link>
              </li>
              <li>
                <Link
                  to="/relationships-social"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-relationships-social"
                >
                  Relationships &amp; social anxiety
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3, Editorial */}
          <div>
            <div
              className="text-xs font-semibold uppercase tracking-widest mb-3"
              style={headingStyle}
            >
              Editorial
            </div>
            <ul className="space-y-2 text-sm list-none p-0 m-0">
              <li>
                <Link
                  to="/editorial-standards"
                  style={linkStyle}
                  className="hover:underline"
                >
                  Editorial standards
                </Link>
              </li>
              <li>
                <Link
                  to="/editorial-process"
                  style={linkStyle}
                  className="hover:underline"
                >
                  Editorial process
                </Link>
              </li>
              <li>
                <Link
                  to="/corrections"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-corrections"
                >
                  Corrections
                </Link>
              </li>
              <li>
                <Link
                  to="/evidence-methodology"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-evidence-methodology"
                >
                  Evidence methodology
                </Link>
              </li>
              <li>
                <Link
                  to="/ai-use"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-ai-use"
                >
                  AI use policy
                </Link>
              </li>
              <li>
                <Link
                  to="/disclosures"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-disclosures"
                >
                  Disclosures
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4, Legal */}
          <div>
            <div
              className="text-xs font-semibold uppercase tracking-widest mb-3"
              style={headingStyle}
            >
              Legal
            </div>
            <ul className="space-y-2 text-sm list-none p-0 m-0">
              <li>
                <Link
                  to="/terms-of-use"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-terms"
                >
                  Terms of use
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  style={linkStyle}
                  className="hover:underline"
                >
                  Privacy policy
                </Link>
              </li>
              <li>
                <Link
                  to="/cookie-policy"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-cookies"
                >
                  Cookie policy
                </Link>
              </li>
              <li>
                <Link
                  to="/medical-disclaimer"
                  style={linkStyle}
                  className="hover:underline"
                >
                  Medical disclaimer
                </Link>
              </li>
              <li>
                <Link
                  to="/accessibility"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-accessibility"
                >
                  Accessibility
                </Link>
              </li>
              <li>
                <Link
                  to="/international"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-international"
                >
                  International notice
                </Link>
              </li>
              <li>
                <Link
                  to="/user-content"
                  style={linkStyle}
                  className="hover:underline"
                  data-testid="footer-link-user-content"
                >
                  Community guidelines
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/*
        ShrinkDaily inbound-contract footer-crosslink entries. The
        component filters by the current route, so the homepage entry
        (e-0001) only renders on `/`. Nothing renders on other routes
        unless the contract publishes a new footer-crosslink for them.
        See src/lib/shrinkdailyInbound.ts.
      */}
      <div className="mx-auto max-w-[1200px] px-6">
        <InboundReciprocalLink placement="footer-crosslink" />
      </div>

      {/* Reusable Shrink Network band, shown on every page */}
      <NetworkFooter />

      {/* Bottom bar */}
      <div
        style={{
          background: "hsl(217 26% 14%)",
          borderTop: "1px solid hsl(var(--footer-border))",
        }}
      >
        <div className="mx-auto max-w-[1200px] px-6 py-4 flex flex-col md:flex-row md:justify-between gap-2 text-xs">
          <div style={{ color: "hsl(var(--footer-muted))" }}>
            &copy; {year} AnxietyResource. All rights reserved.
          </div>
          <div style={{ color: "hsl(var(--footer-muted))" }}>
            Published by{" "}
            <a
              href={networkUrl(SHRINKMD_URL, "footer")}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "hsl(var(--footer-fg))" }}
            >
              shrinkMD Publishing
            </a>{" "}
            &middot; Reviewed by{" "}
            <a
              href={networkUrl(REVIEWER.profileUrl, "footer")}
              target="_blank"
              rel="noopener noreferrer me"
              style={{ color: "hsl(var(--footer-fg))" }}
            >
              Shariq Refai, MD, MBA
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
